import React  from "react";
import AddressForm from "./AddressForm";
import { useState } from "react";

const AddressSection = ({
  addresses,
  selectedAddress,
  handleAddressSelection,
  handleEditAddress,
  showAddressForm,
  setShowAddressForm,
  successMessage,
  errorMessage,
  handleCloseModal,
  onUpdateAddresses,
  fetchAddressesByUserID,
  counties,
  setSelectedCounty,
  userID,
  handleAddNewAddress,
  handleUpdateAddresses,
  isLoading
}) => {
  const [showAllAddresses, setShowAllAddresses] = useState(false);

  const toggleAddressDisplay = () => {
    setShowAllAddresses(!showAllAddresses);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 mb-6 animate-fade-in">
      <h2 className="text-xl font-semibold text-gray-800 mb-4">1. Choose a shipping address</h2>

      {/* Add New Address Button */}
      <button
        onClick={handleAddNewAddress}
        className="text-blue-600 hover:text-blue-800 text-sm font-medium mb-4 transition-all duration-200 hover:underline flex items-center gap-1"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
        </svg>
        {showAddressForm ? "Cancel" : "Add New Address"}
      </button>

      {/* Display selected address with change button */}
      {selectedAddress && !showAllAddresses && (
        <div className="mb-4 animate-scale-in">
          <div className="p-4 border-2 border-orange-400 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-lg shadow-sm transition-all duration-200 hover:shadow-md">
            <div className="flex justify-between items-start mb-2">
              <h3 className="font-medium text-gray-800">{selectedAddress.name}</h3>
              {selectedAddress.isDefault === 1 && (
                <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                  Default
                </span>
              )}
            </div>
            <p className="text-gray-600 text-sm">
              {selectedAddress.postalAddress}, {selectedAddress.town}, {selectedAddress.county}
            </p>
            <p className="text-gray-600 text-sm">{selectedAddress.postalCode}</p>
            <p className="text-gray-600 text-sm">{selectedAddress.phoneNumber}</p>
            <div className="flex gap-4 mt-3">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditAddress(selectedAddress);
                }}
                className="text-blue-600 hover:text-blue-800 text-sm transition-all duration-200 hover:underline font-medium"
              >
                Edit address
              </button>
              <button
                onClick={toggleAddressDisplay}
                className="text-orange-600 hover:text-orange-800 text-sm font-medium transition-all duration-200 hover:underline"
              >
                Change address
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      )}

      {/* Address List - shown when no address selected, or when user clicks "Change" */}
      {!showAddressForm && !isLoading && (showAllAddresses || !selectedAddress) && (
        <div className="space-y-3 animate-fade-in">
          {addresses.length === 0 ? (
            <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <svg className="mx-auto h-12 w-12 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <p className="font-medium">No addresses saved yet</p>
              <p className="text-sm">Add your first address to continue.</p>
            </div>
          ) : (
            addresses.map((address) => (
              <div
                key={address.addressID}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                  selectedAddress?.addressID === address.addressID
                    ? "border-orange-400 bg-orange-50 shadow-md transform scale-[1.02]"
                    : "border-gray-200 hover:border-orange-300 hover:shadow-sm"
                }`}
                onClick={() => {
                  handleAddressSelection(address);
                  setShowAllAddresses(false);
                }}
              >
                <div className="flex items-start">
                  <input
                    type="radio"
                    name="address"
                    checked={selectedAddress?.addressID === address.addressID}
                    onChange={() => {}}
                    className="mt-1 mr-3 h-4 w-4 text-orange-500 focus:ring-orange-400"
                  />
                  <div className="flex-1">
                    <div className="flex justify-between">
                      <h3 className="font-medium text-gray-800">{address.name}</h3>
                      {address.isDefault === 1 && (
                        <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                          Default
                        </span>
                      )}
                    </div>
                    <p className="text-gray-600 text-sm mt-1">
                      {address.postalAddress}, {address.town}, {address.county}
                    </p>
                    <p className="text-gray-600 text-sm">{address.postalCode}</p>
                    <p className="text-gray-600 text-sm">{address.phoneNumber}</p>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditAddress(address);
                      }}
                      className="text-blue-600 hover:text-blue-800 text-sm mt-2 transition-all duration-200 hover:underline font-medium"
                    >
                      Edit address
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Address Form */}
      {showAddressForm && (
        <AddressForm
          userID={userID}
          handleEditAddress={handleEditAddress}
          onUpdateAddresses={onUpdateAddresses}
          counties={counties}
          setSelectedCounty={setSelectedCounty}
          setShowAddressForm={setShowAddressForm}
          isEditing={Boolean(selectedAddress)}
          initialData={selectedAddress || null}
        />
      )}
    </div>
  );
};

export default AddressSection;